<template>
  <div class="now-playing-host">
  <iframe v-if="initialized" ref="player"
    class="official-amll-player" :class="{ 'is-open': presented, 'salt-shared-player': saltShared }" :style="frameStyle"
    :aria-hidden="String(!runtime.ui.lyricsOpen)" :inert="!runtime.ui.lyricsOpen || runtime.ui.queueOpen || runtime.ui.audioSettingsOpen || downloads.visible || playlistTarget.visible" :tabindex="runtime.ui.lyricsOpen ? 0 : -1"
    src="/player/?embed=1" title="AMLL 官方播放页面"
    allow="autoplay; fullscreen" @load="playerLoaded" />
  <img v-if="coverSource && presentation.coverMoving" class="salt-shared-cover" data-salt-shared-cover :src="coverSource" :style="coverStyle" alt="" aria-hidden="true" @error="sharedCoverFallback" />
  </div>
</template>

<script>
import { runtime } from '../bridge';
import { downloads, openDownloads } from '../downloads';
import { playlistTarget } from '../playlist-target';
import { albumRouteId } from '../album-identity';
import { toggleQueue } from '../queue-panel';
import { trackMetadataEditCapability } from '../track-metadata-edit';
import { artistNavigation, trackArtistItems } from '../artist-navigation';
import { workspace, navigationUI } from '../workspace';
import { appleOnly, lockedPageStyle, saltOnly } from '../product-flavor';
import { saltPlayerPresentation } from '../salt/player-presentation';

const storedPlayerStyle=()=>{
  if (saltOnly) {
    // The embedded AMLL app reads the same-origin key before its first render.
    // Fixing the host-side value keeps the Salt presentation across reloads
    // before the host presentation protocol is ready.
    try { localStorage.setItem('simple-music.playerPageStyle', JSON.stringify('salt')); } catch { /* Storage may be unavailable in private contexts. */ }
    return 'salt';
  }
  if (appleOnly) return 'apple';
  try{return JSON.parse(localStorage.getItem('simple-music.playerPageStyle'))==='salt'?'salt':'apple';}catch{return 'apple';}
};

export default {
  name: 'NowPlaying',
  data() { return { runtime, downloads, playlistTarget, workspace, navigationUI, presentation:saltPlayerPresentation, pageStyle:storedPlayerStyle(), frameStyle:{}, coverStyle:{}, coverSource:'', initialized: false, presented: false, ready: false, timer: null, returnFocus: null }; },
  computed: {
    saltShared() { return workspace.appearance==='salt' && this.pageStyle==='salt'; },
    metadata() {
      const track = runtime.state.tracks.find(item => item.id === runtime.state.currentId);
      return {
        channel: 'mineradio-amll', type: 'track', id: track?.id || null,
        title: track?.title || '选择歌曲开始播放', album: track?.album || '',
        artists: track?.artists || [], cover: runtime.cover(track),
        lyrics: runtime.effectiveAmllLyrics(track),
        plainLyrics: track?.metadata?.embeddedPlainLyrics || '',
        translationState: track ? { ...runtime.lyricTranslation(track.id) } : { status: 'idle', source: '', error: '' },
        albumId: track ? albumRouteId(track) : '',
        metadataEdit: trackMetadataEditCapability(track),
      };
    },
  },
  watch: {
    'runtime.ui.playbackRate'() { this.sendSnapshot(); },
    'runtime.state.lyricOffsetMs'() { this.sendSnapshot(); },
    'runtime.ui.playerPrepared'(prepared) { if (prepared) this.initialized = true; },
    'workspace.appearance'() { this.configurePresentation(); if(this.saltShared && runtime.ui.lyricsOpen)this.prepareShared(true);else this.clearShared(); },
    'navigationUI.mobile'() { this.$nextTick(this.resizePresentation); },
    'metadata.id'() { if(this.presentation.active)this.$nextTick(()=>this.prepareShared(runtime.ui.lyricsOpen)); },
    'metadata.cover'(cover) { if(this.presentation.coverMoving)this.coverSource=cover; },
    'runtime.ui.lyricsOpen'(opened) {
      if (opened) {
        this.returnFocus = document.activeElement instanceof HTMLElement && document.activeElement !== document.body
          ? document.activeElement : document.querySelector('[data-nav="now-playing"]');
        document.activeElement?.blur?.();
        this.initialized = true;
        if (runtime.state.currentId) void runtime.loadTrackLyrics(runtime.state.currentId);
      }
      this.$nextTick(() => {
        if (this.saltShared) { this.sendSnapshot(); this.prepareShared(opened); return; }
        // Measure the initial offscreen state even on the first cold open, so
        // the compositor has a real starting position for the upward motion.
        this.$refs.player?.getBoundingClientRect();
        this.presented = opened;
        this.sendSnapshot();
        this.$nextTick(() => {
          if (opened !== runtime.ui.lyricsOpen) return;
          // The prewarmed frame may already be ready, but visibility:hidden
          // still applies until Vue flushes the presented class above.
          if (opened && this.ready) this.focusPlayer();
          else if (!opened) {
            const target = this.returnFocus?.isConnected && this.returnFocus.getClientRects().length
              ? this.returnFocus : document.querySelector('[data-nav="now-playing"]');
            target?.focus();
          }
        });
      });
    },
  },
  mounted() {
    this.initialized = runtime.ui.lyricsOpen || runtime.ui.playerPrepared || Boolean(runtime.state.currentId);
    this._lastMetadata = null;
    if (runtime.ui.lyricsOpen) this.$nextTick(() => { this.$refs.player?.getBoundingClientRect(); this.presented = true; });
    window.addEventListener('message', this.receive);
    window.addEventListener('resize',this.resizePresentation);
    window.addEventListener('simple-music:host-player-menu',this.hostMenu);
    this.geometryObserver=new ResizeObserver(()=>{
      if(!this.presentation.active || this.geometryFrame)return;
      this.geometryFrame=requestAnimationFrame(()=>{this.geometryFrame=0;this.resizePresentation();});
    });
    const main=document.querySelector('main');if(main)this.geometryObserver.observe(main);
    this.motionQuery=window.matchMedia('(prefers-reduced-motion: reduce)');
    this.motionQuery.addEventListener('change',this.motionChanged);
    this.timer = window.setInterval(() => this.sendSnapshot(), 150);
  },
  beforeDestroy() {
    this._keyboardFrame?.removeEventListener('keydown', this.forwardPlayerKey);
    window.removeEventListener('message', this.receive);
    window.removeEventListener('resize',this.resizePresentation);
    window.removeEventListener('simple-music:host-player-menu',this.hostMenu);
    this.motionQuery?.removeEventListener('change',this.motionChanged);
    this.geometryObserver?.disconnect();cancelAnimationFrame(this.geometryFrame);
    this.clearShared();
    window.clearInterval(this.timer);
  },
  methods: {
    playerLoaded() {
      this._keyboardFrame?.removeEventListener('keydown', this.forwardPlayerKey);
      try { this._keyboardFrame = this.$refs.player?.contentWindow; this._keyboardFrame?.addEventListener('keydown', this.forwardPlayerKey); } catch { this._keyboardFrame = null; }
      this.sendSnapshot(true);
    },
    forwardPlayerKey(event) { if (runtime.ui.lyricsOpen && !event.defaultPrevented) this.$emit('player-keydown', event); },
    focusPlayer() {
      const frame=this.$refs.player,active=document.activeElement;
      if(!runtime.ui.lyricsOpen || !this.presented || !frame || frame.inert)return;
      // Late ready/animation callbacks must not steal focus from host controls
      // the listener has already opened while the player was loading.
      if(active && active!==document.body && active!==document.documentElement && active!==frame)return;
      frame.focus({preventScroll:true});
    },
    sharedCoverFallback(event) { const fallback=new URL(runtime.cover(),location.href).href;if(event.target.src!==fallback)event.target.src=fallback; },
    postPresentation(data) { this.$refs.player?.contentWindow?.postMessage({channel:'mineradio-amll',...data},location.origin); },
    configurePresentation() { this.postPresentation({type:'presentation-config',externalDock:workspace.appearance==='salt',saltOnly,lockedPageStyle}); },
    hostMenu(event) {
      if(!this.presentation.active)return;
      this.hostMenuTrigger=document.activeElement instanceof HTMLElement?document.activeElement:null;
      this.postPresentation({type:'presentation-menu',...event.detail});
    },
    motionChanged() { if(this.presentation.coverMoving && this.motionQuery.matches)this.finishShared(runtime.ui.lyricsOpen); },
    resizePresentation() {
      if(!this.presentation.active)return;
      if(this.presentation.coverMoving)this.prepareShared(runtime.ui.lyricsOpen);
      else {this.sharedBase=this.baseRects();this.drawShared(this.presentation.progress);}
    },
    baseRects() {
      const dock=document.querySelector('#bottom-bar'),small=document.querySelector('#control-cover'),main=document.querySelector('main');
      if(!dock || !small || !main)return null;
      const d=dock.getBoundingClientRect(),s=small.getBoundingClientRect();
      const contentLeft=navigationUI.mobile?0:main.getBoundingClientRect().left;
      // Changing left (rather than transforming the footer) preserves the
      // viewport containing block of the existing fixed popup controls.
      const shift=navigationUI.mobile?0:(contentLeft-(parseFloat(getComputedStyle(dock).left)||0))/2;
      return {contentLeft,dock:{x:d.x+shift,y:d.y,width:d.width,height:d.height},small:{x:s.x+shift,y:s.y,width:s.width,height:s.height}};
    },
    prepareShared(opened) {
      cancelAnimationFrame(this.presentationFrame);clearTimeout(this.presentationTimeout);
      const base=this.baseRects();
      if(!base)return;
      if(!opened && this.presentation.progress<.001 && !this.presentation.coverMoving) {this.finishShared(false);return;}
      if(!opened && !this.presentation.active) { this.presented=false; return; }
      const root=this.sharedGeometryRoot;
      const sameGeometry=root && root.trackId===runtime.state.currentId && root.width===innerWidth && root.height===innerHeight;
      const reusable=this.presentation.coverMoving && sameGeometry && this.presentationGeometry?.root===root?this.presentationGeometry.data:null;
      if(!sameGeometry) {this.sharedGeometryRoot=null;this.presentationGeometry=null;}
      // Only an unfinished first opening owns reusable endpoints. Once the
      // full page settles, its independent cover/lyrics layout can change.
      if(opened && !this.presentation.active && this.presentation.progress<.001)this.sharedGeometryRoot={trackId:runtime.state.currentId,width:innerWidth,height:innerHeight};
      this.sharedBase=base;
      this.presentation.baseLeft=base.contentLeft;
      this.presentation.active=true;
      this.presented=true;
      this.transitionId=(this.transitionId||0)+1;
      this.transitionTrack=runtime.state.currentId;
      const current=document.querySelector('[data-salt-shared-cover]')?.getBoundingClientRect();
      this.coverFrom=current?{x:current.x,y:current.y,width:current.width,height:current.height}:null;
      this.coverTo=null;
      this.reusedTransitionId=reusable?this.transitionId:null;
      this.drawShared(this.presentation.progress);
      this.sendSnapshot();this.configurePresentation();
      const transitionId=this.transitionId;
      this.presentationTimeout=setTimeout(()=>{
        if(transitionId!==this.transitionId)return;
        // Geometry is required for the shared transition. A slow frame must
        // not be reported as an animation that reached its final position.
        this.clearShared();runtime.ui.lyricsOpen=false;this.presented=false;this.restoreFocus();
        runtime.notify('播放页仍在加载，请稍后重新打开。','info');
      },8000);
      // The frame still receives the new ID so its cover visibility messages
      // stay ordered, but a rapid reversal need not await another two RAFs.
      if(this.ready)this.postPresentation({type:'presentation-measure',transitionId,trackId:this.transitionTrack});
      if(reusable)this.animateShared({...reusable,transitionId});
    },
    drawShared(progress) {
      this.presentation.progress=progress;
      const base=this.sharedBase;
      if(!base)return;
      this.presentation.left=base.contentLeft*(1-progress);
      const d=base.dock;
      // The iframe and the shared cover use the same progress. Clipping alone
      // leaves the full page fully opaque until the final frame, which makes
      // the close handoff look like a flash. Fade the page with the geometry
      // while the dock content fades in on the inverse value.
      const opacity=Math.max(0,Math.min(1,progress));
      this.frameStyle={clipPath:`inset(${d.y*(1-progress)}px ${Math.max(0,innerWidth-d.x-d.width)*(1-progress)}px ${Math.max(0,innerHeight-d.y-d.height)*(1-progress)}px ${d.x*(1-progress)}px round ${32*(1-progress)}px)`,opacity};
      if(this.coverFrom && this.coverTo) {
        const distance=this.animationTarget-this.animationStart;
        const ratio=distance?Math.min(1,Math.max(0,(progress-this.animationStart)/distance)):1;
        const mix=key=>this.coverFrom[key]+(this.coverTo[key]-this.coverFrom[key])*ratio;
        const width=mix('width'),height=mix('height');
        // Keep the shared image's layout box fixed and move/resize it with a
        // compositor transform. Updating left/top/width/height on every RAF
        // forces layout on SwiftShader during the iframe clip transition.
        const scaleX=this.coverFrom.width ? width/this.coverFrom.width : 1;
        const scaleY=this.coverFrom.height ? height/this.coverFrom.height : 1;
        this.coverStyle={left:this.coverFrom.x+'px',top:this.coverFrom.y+'px',width:this.coverFrom.width+'px',height:this.coverFrom.height+'px',transform:`translate3d(${mix('x')-this.coverFrom.x}px,${mix('y')-this.coverFrom.y}px,0) scale(${scaleX},${scaleY})`,transformOrigin:'top left',borderRadius:(4+8*progress)+'px','--shared-reflection-strength':innerWidth<=innerHeight ? .17*(1-progress) : .17};
      }
    },
    animateShared(data) {
      if(data.transitionId!==this.transitionId || data.trackId!==runtime.state.currentId || data.pageStyle!=='salt' || !this.saltShared)return;
      const rect=data.coverRect;
      if(!rect || !['x','y','width','height'].every(key=>Number.isFinite(rect[key])) || rect.width<=0 || rect.height<=0)return;
      if(this.sharedGeometryRoot)this.presentationGeometry={root:this.sharedGeometryRoot,data};
      clearTimeout(this.presentationTimeout);
      const opened=runtime.ui.lyricsOpen;
      const base=this.baseRects();if(!base)return this.finishShared(opened);
      this.sharedBase=base;
      this.presentation.baseLeft=base.contentLeft;
      this.coverFrom=this.coverFrom || (opened?base.small:rect);
      this.coverTo=opened?rect:base.small;
      this.animationStart=this.presentation.progress;this.animationTarget=opened?1:0;
      this.coverSource=this.metadata.cover;
      this.presentation.coverMoving=true;
      this.drawShared(this.presentation.progress);
      if(this.motionQuery.matches || Math.abs(this.animationTarget-this.animationStart)<.001)return this.finishShared(opened);
      const started=performance.now(),from=this.animationStart,target=this.animationTarget;
      const transitionId=this.transitionId;
      const step=now=>{
        if(transitionId!==this.transitionId)return;
        // Keep the shared image on screen for several compositor frames so
        // the host dock and iframe cover remain visibly synchronized. This
        // also leaves enough time for a second click/Escape to reverse from
        // the actual current geometry instead of jumping to an endpoint.
        const elapsed=Math.max(0, now-started), progress=Math.min(1, elapsed/500);
        // Keep the geometry visible across slow software-rendered frames. The
        // previous ease-out reached most of the endpoint in its first frame,
        // which made the shared cover appear to jump when RAF cadence dropped.
        const next=from+(target-from)*progress;
        if(progress>=1) {this.finishShared(opened);return;}
        this.drawShared(next);this.presentationFrame=requestAnimationFrame(step);
      };
      this.$nextTick(()=>{
        if(transitionId!==this.transitionId)return;
        // Mount the replacement image before hiding either real cover; the
        // same-position overlap avoids a blank frame on close or reversal.
        this.postPresentation({type:'presentation-cover',transitionId,hidden:true});
        this.presentationFrame=requestAnimationFrame(step);
      });
    },
    finishShared(opened) {
      cancelAnimationFrame(this.presentationFrame);clearTimeout(this.presentationTimeout);
      this.sharedGeometryRoot=null;this.presentationGeometry=null;
      this.drawShared(opened?1:0);
      this.presentation.coverMoving=false;this.coverSource='';this.coverFrom=null;this.coverTo=null;this.presentation.baseLeft=0;
      this.postPresentation({type:'presentation-cover',transitionId:this.transitionId,hidden:false});
      this.presented=opened;this.presentation.active=opened;
      this.$nextTick(()=>{
        if(opened!==runtime.ui.lyricsOpen)return;
        if(opened)this.focusPlayer();
        else this.restoreFocus();
      });
    },
    restoreFocus() {
      const target=this.returnFocus?.isConnected && this.returnFocus.getClientRects().length?this.returnFocus:document.querySelector('[data-nav="now-playing"]');
      target?.focus({preventScroll:true});
    },
    clearShared() {
      cancelAnimationFrame(this.presentationFrame);clearTimeout(this.presentationTimeout);
      this.sharedGeometryRoot=null;this.presentationGeometry=null;this.reusedTransitionId=null;
      this.transitionId=(this.transitionId||0)+1;
      this.presentation.active=false;this.presentation.progress=0;this.presentation.coverMoving=false;this.presentation.menuOpen=false;
      this.coverSource='';this.coverFrom=null;this.coverTo=null;this.frameStyle={};
      this.postPresentation({type:'presentation-cover',hidden:false});
      this.presented=runtime.ui.lyricsOpen;
    },
    sendSnapshot(force = false) {
      const target = this.$refs.player?.contentWindow;
      if (!target) return;
      const state = runtime.state;
      const metadata = this.metadata;
      if (force || metadata !== this._lastMetadata) {
        // Vue's computed value changes only with track metadata/lyrics/offset.
        // Avoid mapping and serializing every word on each playback timer tick.
        // Identity, lyrics and their clock must arrive in the same message.
        // A separate state task can otherwise initialize new lyrics at the
        // previous song's position, especially during an instrumental intro.
        target.postMessage(JSON.parse(JSON.stringify({ ...metadata,
          position: state.currentTime * 1000, duration: state.duration * 1000,
          playing: state.isPlaying,
        })), window.location.origin);
        this._lastMetadata = metadata;
      }
      target.postMessage({
        channel: 'mineradio-amll', type: 'state', playing: state.isPlaying,
        position: state.currentTime * 1000, duration: state.duration * 1000,
        positionRevision: runtime.ui.positionRevision,
        volume: runtime.audio.muted ? 0 : state.volume, mode: state.playMode,
        playbackRate: runtime.ui.playbackRate, lyricOffsetMs: state.lyricOffsetMs,
        opened: runtime.ui.lyricsOpen, motion: state.motionLevel,
      }, window.location.origin);
    },
    receive(event) {
      if (event.origin !== window.location.origin || event.source !== this.$refs.player?.contentWindow) return;
      const data = event.data;
      if (data?.channel !== 'mineradio-amll') return;
      if (data.type === 'ready') {
        this.ready = true;
        this.configurePresentation();
        if(this.saltShared && (runtime.ui.lyricsOpen || this.presentation.active))this.$nextTick(()=>this.prepareShared(runtime.ui.lyricsOpen));
        this.$nextTick(this.focusPlayer);
        return this.sendSnapshot(true);
      }
      if(data.type==='presentation-style') {
        const old=this.pageStyle;this.pageStyle=lockedPageStyle || (saltOnly || data.pageStyle==='salt'?'salt':'apple');this.configurePresentation();
        if(!this.saltShared)this.clearShared();
        else if(runtime.ui.lyricsOpen && old!==this.pageStyle) {this.presentation.progress=1;this.$nextTick(()=>this.prepareShared(true));}
        else if(runtime.ui.lyricsOpen && this.presentation.active && !this.presentation.coverMoving && this.presentation.progress<1)this.$nextTick(()=>this.prepareShared(true));
        return;
      }
      if(data.type==='presentation-geometry') {if(data.transitionId!==this.reusedTransitionId)this.animateShared(data);return;}
      if(data.type==='presentation-menu-state') {
        if(this.presentation.menuOpen && !data.open && this.hostMenuTrigger?.isConnected && runtime.ui.lyricsOpen) {
          this.hostMenuTrigger.focus({preventScroll:true});this.hostMenuTrigger=null;
        }
        this.presentation.menuOpen=Boolean(data.open);return;
      }
      if (data.type !== 'command') return;
      const value = Number(data.value);
      switch (data.action) {
        case 'toggle': runtime.togglePlay(); break;
        case 'pause': runtime.pauseCurrentPlayback(); break;
        case 'previous': runtime.nextTrack(-1); break;
        case 'next': runtime.nextTrack(1); break;
        case 'seek': if (Number.isFinite(value)) runtime.seek(value / 1000); break;
        case 'volume':
          if (Number.isFinite(value)) {
            runtime.setVolume(value);
            if (runtime.audio.muted && value > 0) runtime.toggleMute();
          }
          break;
        case 'play-mode': if (['sequence','repeat-all','repeat-one','shuffle'].includes(data.value)) runtime.setPlayMode(data.value); break;
        case 'shuffle': runtime.setPlayMode(runtime.state.playMode === 'shuffle' ? 'sequence' : 'shuffle'); break;
        case 'repeat': {
          const modes = ['sequence', 'repeat-all', 'repeat-one'];
          runtime.setPlayMode(modes[(modes.indexOf(runtime.state.playMode) + 1) % modes.length]);
          break;
        }
        case 'lyric-offset': if (Number.isFinite(value)) runtime.adjustLyricOffset(value); break;
        case 'rate': if (Number.isFinite(value)) runtime.setPlaybackRate(value); break;
        case 'download': {
          const current = runtime.state.tracks.find(track => track.id === runtime.state.currentId);
          if (current) openDownloads([current], '当前歌曲');
          break;
        }
        case 'album': {
          const albumId = runtime.state.currentId ? (() => {
            const current = runtime.state.tracks.find(track => track.id === runtime.state.currentId);
            return current ? albumRouteId(current) : '';
          })() : '';
          runtime.ui.lyricsOpen = false;
          if (albumId) this.$router.push({ name: 'album', params: { id: albumId } }).catch(() => {});
          else this.$router.push('/library?tab=albums').catch(() => {});
          break;
        }
        case 'artist': {
          if (data.trackId !== runtime.state.currentId || !Number.isInteger(value) || value < 0) break;
          const current = runtime.state.tracks.find(track => track.id === runtime.state.currentId);
          const artist = trackArtistItems(current)[value];
          if (!artist) break;
          const target = artistNavigation(artist, workspace.mode);
          if (!target.route) { runtime.notify(target.reason, 'info'); break; }
          runtime.ui.lyricsOpen = false;
          this.$router.push(target.route).catch(() => {});
          break;
        }
        case 'remove-queue': runtime.removeCurrentFromQueue(); break;
        case 'translate-lyrics': if (runtime.state.currentId) void runtime.requestLyricTranslation(runtime.state.currentId); break;
        case 'close': runtime.ui.lyricsOpen = false; break;
        case 'edit': {
          const track = runtime.state.tracks.find(item => item.id === runtime.state.currentId);
          const capability = trackMetadataEditCapability(track);
          if (!capability.editable) { runtime.notify(capability.reason, 'info'); break; }
          const id = track.id;
          runtime.ui.lyricsOpen = false;
          this.$router.push('/library').catch(() => {}).then(() => { runtime.ui.editTrackId = id; });
          break;
        }
        case 'queue': toggleQueue(undefined, true); break;
        case 'audio-settings': runtime.ui.queueOpen = false; runtime.ui.audioSettingsOpen = true; break;
      }
      this.sendSnapshot();
    },
  },
};
</script>

<style scoped>
.official-amll-player { position:fixed; inset:0; width:100%; height:100%; border:0; z-index:220; background:var(--color-body-bg); transform:translate3d(0,100%,0); visibility:hidden; pointer-events:none; border-radius:18px 18px 0 0; transition:transform 280ms cubic-bezier(.2,.75,.2,1),border-radius 280ms,visibility 0s 280ms; }
.official-amll-player.is-open { transform:translate3d(0,0,0); visibility:visible; pointer-events:auto; border-radius:0; transition-delay:0s; }
.official-amll-player.salt-shared-player { transform:none;transition:none;border-radius:0;will-change:clip-path,opacity; }
.salt-shared-cover { position:fixed;z-index:224;object-fit:cover;pointer-events:none;box-shadow:0 14px 40px #0003;will-change:transform,opacity;transform-origin:top left;-webkit-box-reflect:below 0px linear-gradient(transparent 55%,rgb(255 255 255 / var(--shared-reflection-strength,.17))); }
@media(prefers-reduced-motion:reduce) { .official-amll-player { transition:none; } }
</style>
